import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { AdminPermission } from '@shared/roles';
import type { AdminSessionResponse } from '@shared/types';
import { api, setUnauthorizedHandler } from '../lib/api';

/**
 * 后台会话状态。
 *
 * 会话本体是 HttpOnly Cookie；这里只缓存「当前员工 + 权限」，用于渲染导航与按钮。
 * 任何请求遇到 401 都会把状态重置为匿名。
 */

type Status = 'loading' | 'anonymous' | 'authenticated';

interface AuthValue {
  status: Status;
  session: AdminSessionResponse | null;
  login: (email: string, password: string, code: string) => Promise<AdminSessionResponse>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  setSession: (session: AdminSessionResponse) => void;
  can: (permission: AdminPermission) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [session, setSessionState] = useState<AdminSessionResponse | null>(null);

  const refresh = useCallback(async () => {
    try {
      const next = await api.get<AdminSessionResponse>('/api/auth/me');
      setSessionState(next);
      setStatus('authenticated');
    } catch {
      setSessionState(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    const unregister = setUnauthorizedHandler(() => {
      setSessionState(null);
      setStatus('anonymous');
    });
    void refresh();
    return unregister;
  }, [refresh]);

  const login = useCallback(async (email: string, password: string, code: string) => {
    const next = await api.post<AdminSessionResponse>('/api/auth/login', { email, password, code });
    setSessionState(next);
    setStatus('authenticated');
    return next;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } finally {
      setSessionState(null);
      setStatus('anonymous');
    }
  }, []);

  const can = useCallback(
    (permission: AdminPermission) => session?.permissions.includes(permission) ?? false,
    [session],
  );

  const value = useMemo<AuthValue>(
    () => ({
      status,
      session,
      login,
      logout,
      refresh,
      setSession: (next) => {
        setSessionState(next);
        setStatus('authenticated');
      },
      can,
    }),
    [status, session, login, logout, refresh, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth 必须在 AuthProvider 内使用');
  return value;
}
